export const MOBILE_REQUIREMENTS_PASSED_KEY = "mobileRequirementsPassed";

const getMobileRequirementsPassedKey = (gameHash, teamCode) => {
  if (!gameHash || !teamCode) {
    return null;
  }

  return `${MOBILE_REQUIREMENTS_PASSED_KEY}:${gameHash}:${teamCode}`;
};

export const MOBILE_REQUIREMENTS_BYPASS_EMAILS = [
  "kevin.rudissaar@eek.ee",
];

export const isMobileRequirementsBypassedForEmail = (email) =>
  MOBILE_REQUIREMENTS_BYPASS_EMAILS.includes(
    String(email || "").trim().toLowerCase()
  );

export const MOBILE_REQUIREMENT_KEYS = [
  "hasGeolocation",
  "gpsEnabled",
  "hasMotionSensors",
  "hasStorageAccess",
  "hasCookieAccess",
  "onlineStatus",
  "hasCameraPermission",
];

export const createInitialMobileRequirements = () => ({
  isMobile: false,
  isAndroid: false,
  isiOS: false,
  hasGeolocation: false,
  gpsEnabled: false,
  hasMotionSensors: false,
  hasStorageAccess: false,
  hasCookieAccess: false,
  onlineStatus: typeof navigator !== "undefined" ? navigator.onLine : false,
  hasCameraPermission: false,
});

export const areMobileRequirementsMet = (checks) =>
  MOBILE_REQUIREMENT_KEYS.every((key) => checks[key] === true);

export const hasPassedMobileRequirements = (gameHash, teamCode) => {
  const storageKey = getMobileRequirementsPassedKey(gameHash, teamCode);

  if (!storageKey) {
    return false;
  }

  try {
    return window.sessionStorage.getItem(storageKey) === "1";
  } catch (error) {
    return false;
  }
};

export const markMobileRequirementsPassed = (gameHash, teamCode) => {
  const storageKey = getMobileRequirementsPassedKey(gameHash, teamCode);

  if (!storageKey) {
    return;
  }

  try {
    window.sessionStorage.setItem(storageKey, "1");
    window.sessionStorage.removeItem(MOBILE_REQUIREMENTS_PASSED_KEY);
  } catch (error) {
    // Session storage can be blocked in private browsing modes.
  }
};

export const clearMobileRequirementsPassed = (gameHash, teamCode) => {
  const storageKey = getMobileRequirementsPassedKey(gameHash, teamCode);

  try {
    window.sessionStorage.removeItem(MOBILE_REQUIREMENTS_PASSED_KEY);

    if (storageKey) {
      window.sessionStorage.removeItem(storageKey);
    }
  } catch (error) {
    // Session storage can be blocked in private browsing modes.
  }
};

const getMobileOSChecks = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const isAndroid = /android/i.test(userAgent);
  const isiOS = /iPhone|iPad|iPod/i.test(userAgent);

  return {
    isMobile: isAndroid || isiOS,
    isAndroid,
    isiOS,
  };
};

const checkStorageAccess = () => {
  try {
    const testKey = "mobile-requirements-test";
    localStorage.setItem(testKey, "1");
    localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
};

const checkCookieAccess = () => {
  try {
    document.cookie = "mobileRequirementsTest=1; path=/; SameSite=Lax";
    const cookiesEnabled = document.cookie.includes("mobileRequirementsTest=1");
    document.cookie = "mobileRequirementsTest=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    return cookiesEnabled;
  } catch (error) {
    return false;
  }
};

const checkGpsEnabled = () =>
  new Promise((resolve) => {
    if (!("geolocation" in navigator)) {
      resolve(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      () => resolve(true),
      () => resolve(false),
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 10000,
      }
    );
  });

const checkCameraPermission = async () => {
  if (!navigator.mediaDevices?.getUserMedia) {
    return false;
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch (error) {
    return false;
  }
};

export const checkMobileRequirements = async () => {
  const baseChecks = createInitialMobileRequirements();

  const nextChecks = {
    ...baseChecks,
    ...getMobileOSChecks(),
    hasGeolocation: "geolocation" in navigator,
    hasMotionSensors:
      "DeviceMotionEvent" in window || "DeviceOrientationEvent" in window,
    hasStorageAccess: checkStorageAccess(),
    hasCookieAccess: checkCookieAccess(),
    onlineStatus: navigator.onLine,
  };

  const [gpsEnabled, hasCameraPermission] = await Promise.all([
    checkGpsEnabled(),
    checkCameraPermission(),
  ]);

  return {
    ...nextChecks,
    gpsEnabled,
    hasCameraPermission,
  };
};
